import { useState } from "react";
import { ImagePlus, Pencil, Plus, Trash2, X } from "lucide-react";
import { Drawer, Skeleton } from "../../components/ui";
import { UPLOADS_URL } from "../../config/constants";
import {
  useAddGallery,
  useDeleteGallery,
  useGetAllGalleries,
  useUpdateGallery,
} from "../../query/galleryQuery";
import swal from "../../utils/swal";
import "../teachers/teachers.css";
import "./gallery.css";

const EMPTY = { title: "", caption: "", status: "ACTIVE" };

function photoUrl(name) {
  return name ? `${UPLOADS_URL}/${name}` : "";
}

export default function Galleries() {
  const { data: albums = [], isLoading, isFetching } = useGetAllGalleries();
  const { mutate: addGallery, isPending: isAdding } = useAddGallery();
  const { mutate: updateGallery, isPending: isUpdating } = useUpdateGallery();
  const { mutate: deleteGallery, isPending: isDeleting } = useDeleteGallery();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [existing, setExisting] = useState([]);
  const [removed, setRemoved] = useState([]);
  const [files, setFiles] = useState([]);

  const busy = isAdding || isUpdating || isDeleting;

  const closeDrawer = () => {
    files.forEach((item) => URL.revokeObjectURL(item.preview));
    setOpen(false);
    setEditing(null);
    setForm(EMPTY);
    setExisting([]);
    setRemoved([]);
    setFiles([]);
  };

  const openAdd = () => {
    setEditing(null);
    setForm(EMPTY);
    setExisting([]);
    setRemoved([]);
    setFiles([]);
    setOpen(true);
  };

  const openEdit = (album) => {
    setEditing(album);
    setForm({
      title: album.title || "",
      caption: album.caption || "",
      status: album.status === "INACTIVE" ? "INACTIVE" : "ACTIVE",
    });
    setExisting(album.images || []);
    setRemoved([]);
    setFiles([]);
    setOpen(true);
  };

  const addFiles = (list) => {
    const next = Array.from(list || [])
      .filter((file) => file.type?.startsWith("image/"))
      .map((file) => ({ file, preview: URL.createObjectURL(file) }));
    if (!next.length && list?.length) {
      swal("Oops!", "Please select image files", "error");
      return;
    }
    setFiles((prev) => [...prev, ...next]);
  };

  const save = () => {
    const title = form.title.trim();
    const kept = existing.filter((name) => !removed.includes(name));
    if (!title) {
      swal("Oops!", "Album title is required", "error");
      return;
    }
    if (!kept.length && !files.length) {
      swal("Oops!", "Add at least one photo", "error");
      return;
    }

    const body = new FormData();
    body.append("title", title);
    body.append("caption", form.caption.trim());
    body.append("status", form.status);
    files.forEach((item) => body.append("images", item.file));

    const onError = (error) => swal("Oops!", error.message || "Could not save the album", "error");

    if (editing) {
      body.append("removedImages", JSON.stringify(removed));
      updateGallery(
        { id: editing._id, data: body },
        { onSuccess: () => closeDrawer(), onError }
      );
      return;
    }

    addGallery(body, { onSuccess: () => closeDrawer(), onError });
  };

  const removeAlbum = (album) => {
    swal({
      title: "Delete this album?",
      text: album.title,
      icon: "warning",
      buttons: ["Cancel", "Delete"],
      dangerMode: true,
    }).then((yes) => {
      if (!yes) return;
      deleteGallery(album._id, {
        onError: (error) => swal("Oops!", error.message || "Could not delete the album", "error"),
      });
    });
  };

  return (
    <div className="data-page gallery-page">
      <div className="data-toolbar">
        <div className="data-toolbar-search">
          <h2 className="hol-page-title">Photo albums</h2>
        </div>
        <div className="data-toolbar-actions">
          <button type="button" className="data-primary" onClick={openAdd}>
            <Plus size={18} />
            Add album
          </button>
        </div>
      </div>

      {isLoading ? (
        <Skeleton active paragraph={{ rows: 6 }} />
      ) : albums.length === 0 ? (
        <div className="data-card" style={{ padding: 28 }}>
          <p style={{ margin: 0, color: "#0f1f4b", fontWeight: 600 }}>No albums yet</p>
          <p style={{ margin: "6px 0 0", color: "#8b93a7" }}>
            Albums you add here show in the parent app gallery.
          </p>
        </div>
      ) : (
        <div className={`gallery-grid${isFetching || busy ? " is-fetching" : ""}`}>
          {albums.map((album) => {
            const cover = album.images?.[0];
            const count = album.images?.length || 0;
            return (
              <article key={album._id} className="gallery-card">
                <button type="button" className="gallery-cover" onClick={() => openEdit(album)}>
                  {cover ? <img src={photoUrl(cover)} alt="" /> : <ImagePlus />}
                  <span className="gallery-count">
                    {count} {count === 1 ? "photo" : "photos"}
                  </span>
                </button>
                <div className="gallery-card-body">
                  <div className="gallery-card-copy">
                    <h3>{album.title}</h3>
                    <p>{album.status === "INACTIVE" ? "Hidden from parents" : "Visible to parents"}</p>
                  </div>
                  <div className="gallery-card-actions">
                    <button type="button" className="gallery-icon-btn" onClick={() => openEdit(album)} aria-label="Edit album">
                      <Pencil size={14} />
                    </button>
                    <button type="button" className="gallery-icon-btn is-danger" onClick={() => removeAlbum(album)} aria-label="Delete album">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}

      <Drawer
        open={open}
        onClose={closeDrawer}
        title={editing ? "Edit album" : "Add album"}
        width={420}
        footer={
          <>
            <button type="button" className="bw-drawer-btn is-ghost" onClick={closeDrawer} disabled={busy}>
              Cancel
            </button>
            <button type="button" className="bw-drawer-btn is-primary" onClick={save} disabled={busy}>
              {busy ? "Saving..." : editing ? "Update" : "Save"}
            </button>
          </>
        }
      >
        <label className="hol-drawer-label" htmlFor="gallery-title">
          Title<span>*</span>
        </label>
        <input
          id="gallery-title"
          className="gallery-field"
          value={form.title}
          onChange={(event) => setForm((prev) => ({ ...prev, title: event.target.value }))}
          placeholder="Sports day"
        />

        <label className="hol-drawer-label" htmlFor="gallery-caption">
          Caption
        </label>
        <textarea
          id="gallery-caption"
          className="gallery-field"
          rows={3}
          value={form.caption}
          onChange={(event) => setForm((prev) => ({ ...prev, caption: event.target.value }))}
          placeholder="Optional note for parents"
        />

        <p className="hol-drawer-label">Show in the parent app</p>
        <div className="gallery-status">
          {[
            ["ACTIVE", "Visible"],
            ["INACTIVE", "Hidden"],
          ].map(([value, label]) => (
            <button
              key={value}
              type="button"
              className={form.status === value ? "is-on" : ""}
              onClick={() => setForm((prev) => ({ ...prev, status: value }))}
            >
              {label}
            </button>
          ))}
        </div>

        <p className="hol-drawer-label">Photos</p>
        <div className="gallery-photos">
          {existing
            .filter((name) => !removed.includes(name))
            .map((name) => (
              <div key={name} className="gallery-photo">
                <img src={photoUrl(name)} alt="" />
                <button type="button" aria-label="Remove photo" onClick={() => setRemoved((prev) => [...prev, name])}>
                  <X size={12} />
                </button>
              </div>
            ))}
          {files.map((item, index) => (
            <div key={item.preview} className="gallery-photo">
              <img src={item.preview} alt="" />
              <button
                type="button"
                aria-label="Remove photo"
                onClick={() => {
                  URL.revokeObjectURL(item.preview);
                  setFiles((prev) => prev.filter((_, i) => i !== index));
                }}
              >
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
        <label className="gallery-drop">
          <input
            type="file"
            accept="image/png,image/jpeg,image/jpg,image/webp"
            multiple
            onChange={(event) => {
              addFiles(event.target.files);
              event.target.value = "";
            }}
          />
          Add photos
        </label>
      </Drawer>
    </div>
  );
}
